/**
 * An action with no undo, and the confirmation that earns it.
 *
 * Deleting a clinic takes its stores, its articles, its facts and its images
 * with it. There is no restore, so a single click is the wrong interface and a
 * browser `confirm()` is barely better - it is dismissed by reflex and it never
 * says what is about to be lost.
 *
 * So this asks for the name to be TYPED. Not as ceremony: typing 「銀座院」 is the
 * one interaction that cannot be performed by muscle memory on the wrong row,
 * which is the actual failure mode. It also puts the count of what goes with it
 * in front of somebody at the moment they are deciding, which is the only
 * moment it is useful.
 *
 * Used for the handful of genuinely irreversible actions. Retiring a fact and
 * archiving an article stay ordinary buttons, because both are reversible and
 * both are the common case.
 */
import { useState } from 'react';
import { ApiError } from '../api.ts';

export function DangerButton({
  /** What the button says before it is armed. */
  label,
  /** The exact text somebody has to type. Usually the record's own name. */
  confirmText,
  /** What will be lost, in the reader's words. One line per consequence. */
  consequences,
  /** Runs only once the typed text matches. */
  onConfirm,
  /** Told after it succeeds, so the list can re-read or the route can change. */
  onDone,
}: {
  label: string;
  confirmText: string;
  consequences: string[];
  onConfirm: () => Promise<unknown>;
  onDone?: (() => void) | undefined;
}) {
  const [armed, setArmed] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Trimmed on both sides only: a name with an inner space must still match.
  const matches = typed.trim() === confirmText.trim();

  async function run() {
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
      setArmed(false);
      setTyped('');
      onDone?.();
    } catch (err) {
      setError(
        err instanceof ApiError ? [err.message, ...err.fields].join(' / ') : '削除できませんでした',
      );
    } finally {
      setBusy(false);
    }
  }

  if (!armed) {
    return (
      <button
        className="btn btn--ghost btn--sm"
        type="button"
        onClick={() => {
          setArmed(true);
          setError(null);
        }}
      >
        {label}
      </button>
    );
  }

  return (
    <div className="panel" style={{ marginTop: 8 }}>
      <h3 className="panel__title">{label}</h3>
      <p className="muted small" style={{ marginTop: 0 }}>
        この操作は<strong>取り消せません</strong>。次のものが完全に削除されます。
      </p>
      <ul className="plain small" style={{ marginTop: 0 }}>
        {consequences.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>

      <div className="field">
        <label htmlFor="danger-confirm">
          続けるには <code>{confirmText}</code> と入力してください
        </label>
        <input
          id="danger-confirm"
          value={typed}
          autoComplete="off"
          onChange={(event) => setTyped(event.target.value)}
        />
      </div>

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      <div className="editpanel__actions">
        <button
          className="btn btn--sm"
          type="button"
          disabled={!matches || busy}
          onClick={() => void run()}
        >
          {busy ? '削除中…' : '完全に削除する'}
        </button>
        <button
          className="btn btn--ghost btn--sm"
          type="button"
          disabled={busy}
          onClick={() => {
            setArmed(false);
            setTyped('');
            setError(null);
          }}
        >
          やめる
        </button>
      </div>
    </div>
  );
}

/**
 * The same idea, sized for a table row.
 *
 * A fact or a draft article is small and re-creatable, so making somebody type
 * its name would be theatre. But it sits next to 編集 and 承認 in a row of
 * identical buttons, which is exactly where a misclick happens - so the first
 * click only arms it, and the label changes to say what the second click does.
 *
 * Arming is per-button, not per-table: a shared pending flag would grey out
 * every row while one of them worked.
 */
export function ConfirmButton({
  label,
  confirmLabel,
  onConfirm,
}: {
  label: string;
  confirmLabel: string;
  onConfirm: () => Promise<unknown>;
}) {
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);

  return (
    <button
      className={`btn btn--sm ${armed ? '' : 'btn--ghost'}`}
      type="button"
      disabled={busy}
      title={armed ? undefined : label}
      onClick={() => {
        if (!armed) {
          setArmed(true);
          return;
        }
        setBusy(true);
        void onConfirm().finally(() => {
          setBusy(false);
          setArmed(false);
        });
      }}
    >
      {busy ? '…' : armed ? confirmLabel : label}
    </button>
  );
}
